import { useMatomo } from '@datapunt/matomo-tracker-react';
import cx from 'clsx';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import DatePicker from '@components/form/DatePicker/DatePicker';
import ModalHeader from '@components/modals/ModalHeader/ModalHeader';
import Button from '@components/uikit/Button/Button';
import RadioButton from '@components/uikit/RadioButton/RadioButton';
import Tabs from '@components/uikit/Tabs/Tabs';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useReportActionsMutation } from '@services/report/useReportActionsMutation';
import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppRtl } from '@store/useAppStore';
import { useModalActions } from '@store/useModalStore';

import FacilityTreeBlock from '../../common/FacilityTreeBlock/FacilityTreeBlock';
import LocationSelectSwitcher from './components/LocationSelectSwitcher';
import style from './style.module.scss';

const defaultValues = {
  dateRange: [new Date().setMonth(new Date().getMonth() - 1), new Date()],
  with_installation_point_data: '0',
  allTimeChecked: true,
  checkedKeys: [],
  withWatchlist: false,
  checkboxValue: false,
  aiType: 'regular',
  fileFormat: 'pdf',
};

const LocationSelectModal = ({ report }) => {
  const { trackEvent } = useMatomo();
  const { t } = useTranslation();
  const { customerId } = useParams();

  const { data: assetTreeData } = useAssetTreeQuery();
  const { data: profileData } = useUserProfileQuery();

  const currentDateFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;

  const { setModalClose } = useModalActions();

  const { mutate: reportActionsMutate, isLoading } = useReportActionsMutation();

  const [activeRadioBtn, setActiveRadioBtn] = useState('0');

  const { control, setValue, handleSubmit, watch, reset } = useForm({
    mode: 'all',
    defaultValues,
  });

  const [dateRange, allTimeChecked, checkedKeys] = watch(['dateRange', 'allTimeChecked', 'checkedKeys']);

  const isFutureDatesAllowed = !dateRange?.[0] || !!dateRange?.[1];

  const isAnyAdditionalFilter =
    !!report.datarangeTitle || report.extraFileFormat || report?.aiTypeFilter || !!report.byStructureTitle;

  const rtl = selectAppRtl();
  const facilityStructureData = assetTreeData?.facilities;

  const handleClose = () => {
    setModalClose();
    reset();
  };

  const submit = handleSubmit((formData) => {
    const tempChecked = formData.checkedKeys.filter((id) => assetTreeData.allowedIds[id]);

    if (!formData.withWatchlist) {
      if (!tempChecked.length) {
        return showToast({ variant: 'info', message: 'Please check any Facilities' });
      }

      if (formData.dateRange?.length && !formData.dateRange[1]) {
        return showToast({ variant: 'info', message: 'Please choose end date or clear Data Range' });
      }
    }

    const locations = report.locationsType === 'array' ? tempChecked : tempChecked.join(',');

    const dateFormat = 'MM/dd/yyyy';
    const period = formData.allTimeChecked
      ? undefined
      : `${format(formData.dateRange[0], dateFormat)}-${format(formData.dateRange[1], dateFormat)}`;

    const includeClosed =
      (report.action === 'exportCostSavings' ||
        (report.action === 'exportActiveActionItems' && !allTimeChecked)) &&
      formData.checkboxValue;

    showToast({ variant: 'success', message: t('The download will begin shortly.') });

    reportActionsMutate(
      {
        action: report.action,
        locations,
        period: period,
        withInstPointData: formData.with_installation_point_data,
        withWatchList: formData.withWatchlist,
        aiType: formData.aiType || null,
        checkboxValue: +includeClosed,
        customerId,
        fileFormat: formData.fileFormat,
      },
      {
        onSuccess: (result) => {
          const reportFileName = report.byStructureTitle
            ? formData.with_installation_point_data === '0'
              ? `Structure-by-Equipment`
              : `Structure-by-installation-point`
            : `${report.title}`;

          saveAs(
            new Blob([result?.data], {
              type: `text/plain;charset=utf-8`,
            }),
            `${profileData?.customer?.name || ''} ${t(reportFileName)} ${format(
              new Date(),
              currentDateFormat,
            )}.${formData.fileFormat === 'xlsx' ? formData.fileFormat : report.fileFormat.downloadFormat}`,
          );

          reset();
        },
        onSettled: () => handleClose(),
      },
    );

    trackEvent({
      category: 'Download File',
      action: report.action,
      name: report.title,
    });
  });

  const radioChange = (e) => {
    setActiveRadioBtn(e.target.value);
    setValue('allTimeChecked', !allTimeChecked);
  };

  return (
    <div className={style.locationSelectModal}>
      <ModalHeader title={t(report?.title)} />
      <div className={style.modalInfoLine}>
        {t(report.modalDescription || 'Please select the details below, and then click Download.')}
      </div>
      <div className={cx(style.modalBody, { [style.fullHeight]: report.action === 'hardwareOverview' })}>
        <div className={cx({ [style.additionaFilters]: isAnyAdditionalFilter })}>
          {!!report.datarangeTitle && (
            <div className={style.locationSelectModalDates}>
              <div className={style.datepickerBlock}>
                <RadioButton
                  id="0"
                  checked={activeRadioBtn === '0'}
                  value="0"
                  onChange={radioChange}
                  customCls={style.datepickerButton}
                  label={t('Include all data')}
                />
                <div className={style.bottomBlock}>
                  <RadioButton
                    id="1"
                    checked={activeRadioBtn === '1'}
                    onChange={radioChange}
                    value="1"
                    customCls={style.datepickerButton}
                    label={t('Include data from Action Items within date range:')}
                  />
                  <div className={style.datepickerRange}>
                    {!allTimeChecked && (
                      <DatePicker
                        id="daterange-facilities"
                        className={style.daterangeField}
                        placeholderText={t('Select Date Range')}
                        dateFormat={rtl ? 'yyyy/MM/dd' : `${profileData?.dateFormat.date}`}
                        startDate={dateRange?.[0]}
                        endDate={dateRange?.[1]}
                        minDate={new Date(profileData.customer.created_at)}
                        maxDate={isFutureDatesAllowed && new Date()}
                        onChange={(value) => setValue('dateRange', value)}
                        selectsRange
                        type="woHelperText"
                        showMonthDropdown
                        showYearDropdown
                        autoComplete="off"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {report.extraFileFormat && (
            <div className={style.fileFormatSwitcher}>
              <Tabs
                tabsItems={[
                  { value: 'pdf', label: t('PDF') },
                  { value: 'xlsx', label: t('XLSX') },
                ]}
                className={style.fileFormatWrapper}
                control={control}
                name="fileFormat"
                label={t('File format')}
                type={'woHelperText'}
              />
            </div>
          )}

          <div className={style.locationSelectModalFilters}>
            {report?.aiTypeFilter && (
              <div className={style.containsListButtons}>
                <Tabs
                  label={t('What action item type would you like to be included in the report?')}
                  id="ai-type"
                  control={control}
                  name="aiType"
                  tabsItems={[
                    { value: '', label: t('All') },
                    { value: 'regular', label: t('Regular') },
                    { value: 'waitesWireless', label: t('Hardware') },
                  ]}
                  type={'woHelperText'}
                />
              </div>
            )}
            <LocationSelectSwitcher
              action={report.action}
              allTimeChecked={allTimeChecked}
              control={control}
              checkboxLabel={report?.checkboxLabel}
              showCheckbox={report?.showCheckbox}
              aiTypeFilter={report?.aiTypeFilter}
            />
          </div>

          {!!report.byStructureTitle && (
            <div className={style.containsListButtons}>
              <Tabs
                label={t(report.byStructureTitle)}
                id="contains-list"
                control={control}
                name="with_installation_point_data"
                tabsItems={[
                  { value: '0', label: 'Equipment' },
                  { value: '1', label: 'Installation Points' },
                ]}
                type={'woHelperText'}
              />
            </div>
          )}
        </div>
        <FacilityTreeBlock
          reportType={report.action}
          control={control}
          locationsFormName="checkedKeys"
          allFacilitiesFormName="all_facilities"
          facilityStructureData={facilityStructureData}
          variant={report.action}
          showSearch={true}
          showSelectAll={true}
        />
      </div>
      <div className={style.modalFooter}>
        <Button type="secondary" text={t('Cancel')} onClick={handleClose} />
        <Button
          type="primary"
          variant={'medium'}
          disabled={!checkedKeys.length || isLoading}
          text={t(report.btnText)}
          onClick={submit}
        />
      </div>
    </div>
  );
};

export default LocationSelectModal;
